import { type FC, useRef, type ChangeEvent, useState, useEffect } from 'react';
import {
  Box,
  Button,
  FormLabel,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CloseIcon from '@mui/icons-material/Close';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

import { type FormAttachment } from '#apps/client-web/pages/audit-form-mechanical-entry/types';

import { type Attachment } from '#packages/data-collection/models/common/attachment';

import { isImageMimeType, truncateMiddle } from '../../helpers';

export type FileInputProps = {
  accept?: string;
  label?: string;
  value?: FormAttachment[];
  disabled?: boolean;
  error?: boolean;
  multiple?: boolean;
  onChange: (files: Attachment[]) => void;
  /**
   * When provided, removal is not applied immediately. Instead this callback
   * is invoked with the attachment index and a `proceed` function. Call
   * `proceed()` to apply the removal (e.g. after a confirmation dialog).
   */
  onRemoveRequest?: (index: number, proceed: () => void) => void;
};

export const FileInput: FC<FileInputProps> = ({
  accept,
  label,
  value = [],
  disabled = false,
  error = false,
  multiple = true,
  onChange,
  onRemoveRequest,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    setPreviews((previousPreviews) => {
      const hasNew = value.some((item) => !previousPreviews.has(item.name));
      if (!hasNew) return previousPreviews;

      const newPreviews = new Map(previousPreviews);
      value.forEach((item) => {
        if (!newPreviews.has(item.name)) {
          newPreviews.set(item.name, URL.createObjectURL(item.file));
        }
      });
      return newPreviews;
    });
  }, [value]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      previews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    const fileArray = Array.from(files);

    // Generate preview URLs for images
    const newPreviews = new Map(previews);
    fileArray.forEach((file) => {
      if (isImageMimeType(file.type)) {
        newPreviews.set(file.name, URL.createObjectURL(file));
      }
    });
    setPreviews(newPreviews);

    const newAttachments: FormAttachment[] = fileArray.map((file) => ({
      name: file.name,
      mimeType: file.type ?? 'application/octet-stream',
      file: file,
    }));

    const updatedValue = multiple
      ? [...value, ...newAttachments]
      : newAttachments;
    onChange(updatedValue);

    // Reset input so the same file can be selected again
    event.target.value = '';
  };

  const handleToggleSelectedForReport = (index: number) => {
    const attachment = value[index];
    const isCurrentlySelected = attachment.isSelectedForReport ?? false;

    if (!isCurrentlySelected) {
      const selectedCount = value.filter((a) => a.isSelectedForReport).length;
      if (selectedCount >= 4) return;
    }

    const updatedValue = value.map((a, i) =>
      i === index ? { ...a, isSelectedForReport: !isCurrentlySelected } : a,
    );
    onChange(updatedValue);
  };

  const handleRemove = (index: number) => {
    const attachment = value[index];

    const proceed = () => {
      // Revoke preview URL if exists
      const previewUrl = previews.get(attachment.name);
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
        const newPreviews = new Map(previews);
        newPreviews.delete(attachment.name);
        setPreviews(newPreviews);
      }

      const updatedValue = value.filter((_, i) => i !== index);
      onChange(updatedValue);
    };

    if (onRemoveRequest) {
      onRemoveRequest(index, proceed);
    } else {
      proceed();
    }
  };

  return (
    <>
      {label && (
        <FormLabel sx={{ mb: 1 }} error={error} aria-invalid={error}>
          {label}
        </FormLabel>
      )}
      <Box
        sx={{
          border: 1,
          borderColor: error ? 'error.main' : 'divider',
          borderRadius: 1,
          p: 2,
          backgroundColor: disabled
            ? 'action.disabledBackground'
            : 'background.paper',
        }}>
        <Stack spacing={2}>
          <Button
            variant="outlined"
            startIcon={<AttachFileIcon />}
            onClick={handleClick}
            disabled={disabled}
            sx={{ alignSelf: 'flex-start' }}>
            {multiple ? 'Add Files' : 'Select File'}
          </Button>

          <input
            ref={inputRef}
            type="file"
            hidden
            multiple={multiple}
            accept={accept}
            onChange={handleFileChange}
            disabled={disabled}
          />

          {value.length > 0 && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                gap: 2,
              }}>
              {value.map((attachment, index) => {
                const previewUrl = previews.get(attachment.name);
                const isImage = isImageMimeType(attachment.mimeType);

                return (
                  <Box
                    key={`${attachment.name}-${index}`}
                    sx={{
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      p: 1,
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                      backgroundColor: disabled
                        ? 'action.disabledBackground'
                        : 'background.paper',
                    }}>
                    {!disabled && (
                      <IconButton
                        size="small"
                        onClick={() => handleToggleSelectedForReport(index)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          left: -8,
                          backgroundColor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                          color: attachment.isSelectedForReport
                            ? 'warning.main'
                            : 'action.disabled',
                          '&:hover': {
                            backgroundColor: 'warning.light',
                            color: 'warning.contrastText',
                          },
                        }}>
                        {attachment.isSelectedForReport ? (
                          <StarIcon fontSize="small" />
                        ) : (
                          <StarBorderIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}

                    {!disabled && (
                      <IconButton
                        size="small"
                        onClick={() => handleRemove(index)}
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          backgroundColor: 'background.paper',
                          border: 1,
                          borderColor: 'divider',
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.contrastText',
                          },
                        }}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}

                    {isImage && previewUrl ? (
                      <Box
                        component="img"
                        src={previewUrl}
                        alt={attachment.name}
                        sx={{
                          width: 100,
                          height: 100,
                          objectFit: 'cover',
                          borderRadius: 1,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 100,
                          height: 100,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'action.hover',
                          borderRadius: 1,
                        }}>
                        <InsertDriveFileIcon
                          sx={{ fontSize: 48, color: 'text.secondary' }}
                        />
                      </Box>
                    )}

                    <Typography
                      variant="caption"
                      sx={{
                        mt: 1,
                        maxWidth: '100%',
                        textAlign: 'center',
                        wordBreak: 'break-all',
                      }}
                      title={attachment.name}>
                      {truncateMiddle(attachment.name, 18)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          )}
        </Stack>
      </Box>
    </>
  );
};
